import React, { useState, useEffect, useRef } from 'react';
import { X, Printer, Download, Plus, Minus, DollarSign } from 'lucide-react';
import { productosAPI } from '../api/client';
import { useConfig } from '../context/ConfigContext';

const BarcodeGenerator = ({ isOpen, onClose }) => {
  const [productos, setProductos] = useState([]);
  const [selectedProductos, setSelectedProductos] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [jsBarcodeLoaded, setJsBarcodeLoaded] = useState(false);
  const [mostrarPrecio, setMostrarPrecio] = useState(true);
  const canvasRefs = useRef({});
  const { formatCurrency } = useConfig();

  useEffect(() => {
    // Cargar JsBarcode desde CDN
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js';
    script.async = true;
    script.onload = () => setJsBarcodeLoaded(true);
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadProductos();
    }
  }, [isOpen]);

  useEffect(() => {
    // Generar códigos cuando cambian los seleccionados y la librería está cargada
    if (jsBarcodeLoaded && selectedProductos.length > 0) {
      selectedProductos.forEach(item => {
        generarCodigo(item.producto);
      });
    }
  }, [selectedProductos, jsBarcodeLoaded]);

  const loadProductos = async () => {
    try {
      const response = await productosAPI.getAll();
      if (response.data.success) {
        setProductos(response.data.data.filter(p => p.codigo_barras));
      }
    } catch (error) {
      console.error('Error al cargar productos:', error);
    }
  };

  const generarCodigo = (producto) => {
    const canvas = canvasRefs.current[producto.id];
    if (canvas && producto.codigo_barras && window.JsBarcode) {
      try {
        window.JsBarcode(canvas, producto.codigo_barras, {
          format: "CODE128",
          width: 2,
          height: 60,
          displayValue: true,
          fontSize: 16,
          textMargin: 5,
          margin: 10
        });
      } catch (error) {
        console.error("Error generando código:", error);
        // Fallback simple si falla
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0,0,canvas.width, canvas.height);
        ctx.font = "12px Arial";
        ctx.fillText("Error en código", 10, 20);
      }
    }
  };

  const agregarProducto = (producto) => {
    const yaExiste = selectedProductos.find(p => p.producto.id === producto.id);
    if (yaExiste) {
      setSelectedProductos(
        selectedProductos.map(p =>
          p.producto.id === producto.id
            ? { ...p, cantidad: p.cantidad + 1 }
            : p
        )
      );
    } else {
      setSelectedProductos([...selectedProductos, { producto, cantidad: 1 }]);
    }
  };

  const actualizarCantidad = (productoId, nuevaCantidad) => {
    if (nuevaCantidad < 1) return;
    setSelectedProductos(
      selectedProductos.map(p =>
        p.producto.id === productoId
          ? { ...p, cantidad: nuevaCantidad }
          : p
      )
    );
  };

  const eliminarProducto = (productoId) => {
    setSelectedProductos(selectedProductos.filter(p => p.producto.id !== productoId));
  };

  const imprimirCodigos = () => {
    const printWindow = window.open('', '_blank');
    
    // Generar imágenes base64 de todos los códigos
    const codigosData = selectedProductos.map(item => {
      const canvas = canvasRefs.current[item.producto.id];
      return {
        ...item,
        imgData: canvas ? canvas.toDataURL() : ''
      };
    });

    const codigosHTML = codigosData.map(item => {
      return Array(item.cantidad).fill().map(() => `
        <div class="etiqueta">
          <div class="nombre-producto">${item.producto.nombre}</div>
          ${mostrarPrecio ? `<div class="precio-producto">${formatCurrency(item.producto.precio_venta)}</div>` : ''}
          <img src="${item.imgData}" class="codigo-img" />
        </div>
      `).join('');
    }).join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Imprimir Etiquetas</title>
          <style>
            body { margin: 0; padding: 10px; font-family: Arial, sans-serif; }
            
            .contenedor {
              display: flex;
              flex-wrap: wrap;
              gap: 8px;
              justify-content: flex-start;
            }

            .etiqueta {
              width: 200px; /* Ancho compacto ideal para 3 columnas en carta */
              height: 100px;
              border: 1px dashed #ccc;
              padding: 4px;
              text-align: center;
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: center;
              page-break-inside: avoid;
              box-sizing: border-box;
              overflow: hidden;
            }

            .nombre-producto {
              font-size: 11px;
              font-weight: bold;
              margin-bottom: 2px;
              line-height: 1.1;
              max-height: 2.2em;
              overflow: hidden;
              width: 100%;
              word-break: break-word;
            }

            .precio-producto {
              font-size: 14px;
              font-weight: bold;
              color: #111;
              margin-bottom: 2px;
            }

            .codigo-img {
              max-width: 100%;
              height: auto;
              max-height: ${mostrarPrecio ? '60px' : '70px'};
              display: block;
            }

            @media print {
              @page { margin: 0.5cm; }
              body { margin: 0; }
              .etiqueta {
                border: none;
                outline: 1px dashed #ddd; /* Guía de corte tenue */
              }
            }
          </style>
        </head>
        <body>
          <div class="contenedor">
            ${codigosHTML}
          </div>
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
              }, 500);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const descargarPDF = () => {
    imprimirCodigos();
  };

  const productosFiltrados = productos.filter(p =>
    p.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
    p.codigo_barras.includes(busqueda) ||
    p.sku.toLowerCase().includes(busqueda.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-primary-600 text-white px-6 py-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Printer className="w-6 h-6" />
            Generador de Etiquetas
          </h2>
          <button onClick={onClose} className="text-white hover:bg-primary-700 p-2 rounded">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Selección */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Seleccionar Productos</h3>
              <input
                type="text"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar producto..."
                className="input-field mb-4"
              />
              <div className="border rounded-lg max-h-96 overflow-y-auto">
                {productosFiltrados.map(producto => (
                  <div
                    key={producto.id}
                    className="p-3 border-b last:border-b-0 hover:bg-gray-50 cursor-pointer flex justify-between items-center"
                    onClick={() => agregarProducto(producto)}
                  >
                    <div className="flex-1">
                      <p className="font-medium text-sm">{producto.nombre}</p>
                      <p className="text-xs text-gray-600">{producto.codigo_barras}</p>
                    </div>
                    <button className="btn-primary btn-sm">
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Vista Previa */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-semibold">
                  Etiquetas ({selectedProductos.reduce((sum, p) => sum + p.cantidad, 0)})
                </h3>
                
                {/* Toggle para mostrar/ocultar precio */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={mostrarPrecio}
                    onChange={(e) => setMostrarPrecio(e.target.checked)}
                    className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium flex items-center gap-1">
                    <DollarSign className="w-4 h-4" />
                    Mostrar Precio
                  </span>
                </label>
              </div>
              {selectedProductos.length === 0 ? (
                <div className="border rounded-lg p-8 text-center text-gray-500">
                  Selecciona productos para generar etiquetas
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {selectedProductos.map(item => (
                    <div key={item.producto.id} className="border rounded-lg p-3">
                      <div className="flex justify-between mb-2">
                        <div className="flex-1">
                          <span className="font-medium text-sm block">{item.producto.nombre}</span>
                          {mostrarPrecio && (
                            <span className="text-sm text-primary-600 font-semibold">
                              {formatCurrency(item.producto.precio_venta)}
                            </span>
                          )}
                        </div>
                        <button onClick={() => eliminarProducto(item.producto.id)} className="text-red-600">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      
                      {/* Canvas de JsBarcode */}
                      <div className="flex justify-center bg-white p-2 border rounded mb-2">
                        <canvas ref={el => canvasRefs.current[item.producto.id] = el} />
                      </div>

                      <div className="flex items-center gap-2 justify-center">
                        <button onClick={() => actualizarCantidad(item.producto.id, item.cantidad - 1)} className="btn-secondary btn-sm"><Minus className="w-3 h-3"/></button>
                        <span className="w-8 text-center">{item.cantidad}</span>
                        <button onClick={() => actualizarCantidad(item.producto.id, item.cantidad + 1)} className="btn-secondary btn-sm"><Plus className="w-3 h-3"/></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="border-t px-6 py-4 bg-gray-50 flex justify-end gap-3">
          <button onClick={onClose} className="btn-secondary">Cerrar</button>
          <button onClick={imprimirCodigos} disabled={selectedProductos.length === 0} className="btn-primary flex items-center gap-2">
            <Printer className="w-4 h-4" /> Imprimir
          </button>
        </div>
      </div>
    </div>
  );
};

export default BarcodeGenerator;
